import { createFileRoute, Link } from '@tanstack/react-router';
import { useHallOfFame } from '@/hooks/hallOfFameHooks';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Medal, Store, Users, Heart, Package, Calendar, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import FollowButton from '@/components/FollowButton';
import { Badge } from '@/components/ui/badge';
export const Route = createFileRoute('/_regular/hall-of-fame/')({
  component: HallOfFamePage,
  // Not prerendered: this is a live leaderboard, not static content — SSG
  // would freeze it at build time until the next deploy.
  head: () => ({
    meta: [
      { title: 'حائط الشرف | سكيكيمة' },
      {
        name: 'description',
        content: 'أفضل المتاجر والمستخدمين على منصة سكيكيمة.',
      },
    ],
  }),
});

function HallOfFamePage() {
  const { data, isLoading } = useHallOfFame();

  if (isLoading) return <HallOfFameSkeleton />;

  const lastUpdate = data?.updated_at ? format(new Date(data.updated_at), 'dd MMMM yyyy', { locale: ar }) : '';

  // Shared renderer for store entries (includes FollowButton)
  const storeRenderItem = (store, rank, showExtra = true) => (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
          {rank}
        </div>
        <div className="h-10 w-10 rounded-full overflow-hidden bg-muted shrink-0">
          <img src={store.logo} alt={store.name} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <Link to={`/store/${store.slug}`} className="font-medium hover:underline line-clamp-1">
            {store.name}
          </Link>
          <p className="text-xs text-muted-foreground">
            {store.city} • {store.store_type}
          </p>
          {showExtra && store.followers_count !== undefined && (
            <p className="text-xs text-muted-foreground">{store.followers_count} متابع</p>
          )}
          {showExtra && store.products_count !== undefined && (
            <p className="text-xs text-muted-foreground">{store.products_count} منتج</p>
          )}
          {showExtra && store.total_loves !== undefined && (
            <p className="text-xs text-muted-foreground">{store.total_loves} إعجاب على منتجاته</p>
          )}
        </div>
      </div>
      <FollowButton storeSlug={store.slug} initialFollowed={false} />
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8 space-y-12" dir="rtl">
      {/* Header */}
<section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/5 via-background to-primary/5 py-12 md:py-16 lg:py-20">
  <div className="absolute inset-0 bg-grid-small-primary/[0.03] bg-[length:20px_20px] [mask-image:radial-gradient(ellipse_at_center,white,transparent)]" />
  <div className="container relative mx-auto px-4 text-center">
    <Badge variant="outline" className="mb-4 gap-1 border-primary/30 bg-primary/5 text-primary">
      <Medal className="h-3 w-3" />
      حائط الشرف
    </Badge>
    <h1 className="mb-4 text-3xl font-bold tracking-tight md:text-4xl lg:text-5xl">
      حائط المشاهير في{" "}
      <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
        سكيكيمة
      </span>
    </h1>
    <p className="mx-auto max-w-2xl text-base text-muted-foreground md:text-lg">
      تكريمًا لأوائل المنصة وأكثرها نشاطًا وتفاعلاً
    </p>
    <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-muted px-4 py-1.5 text-sm text-muted-foreground">
      <Calendar className="h-4 w-4" />
      <span>آخر تحديث: {lastUpdate}</span>
      <span className="text-xs">(تحديث أسبوعي)</span>
    </div>
    {/* Optional CTA buttons – you can add them later */}
    {/* <div className="mt-8 flex flex-wrap justify-center gap-4">
      <Button size="lg" className="gap-2 rounded-full">عرض جميع المتاجر</Button>
      <Button variant="outline" size="lg" className="gap-2 rounded-full">اكتشف المنتجات</Button>
    </div> */}
  </div>
</section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Column 1: Users & Stores */}
        <div className="space-y-8">
          {/* Earliest Visitors */}
          <LeaderboardCard
            title="أوائل الزوار"
            icon={<Users className="h-5 w-5" />}
            items={data?.earliest_visitors}
            renderItem={(user, rank) => (
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                  {rank}
                </div>
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage src={user.avatar} />
                  <AvatarFallback>{user.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">انضم {user.joined_at}</p>
                </div>
              </div>
            )}
          />

          {/* Earliest Stores */}
          <LeaderboardCard
            title="أوائل المتاجر"
            icon={<Store className="h-5 w-5" />}
            items={data?.earliest_stores}
            renderItem={(store, rank) => storeRenderItem(store, rank, false)}
          />

            {/* Most Loves (total likes on products) */}
          <LeaderboardCard
            title="أكثر المتاجر حبًا"
            icon={<Heart className="h-5 w-5 text-pink-500" />}
            items={data?.most_loves}
            renderItem={(store, rank) => storeRenderItem(store, rank, true)}
          />
        </div>

        {/* Column 2: Store Rankings */}
        <div className="space-y-8">
          {/* Most Followed */}
          <LeaderboardCard
            title="أكثر المتاجر متابعة"
            icon={<Heart className="h-5 w-5 text-red-500" />}
            items={data?.most_followed}
            renderItem={(store, rank) => storeRenderItem(store, rank, true)}
          />

          {/* Most Products */}
          <LeaderboardCard
            title="أكثر المتاجر منتجات"
            icon={<Package className="h-5 w-5" />}
            items={data?.most_products}
            renderItem={(store, rank) => storeRenderItem(store, rank, true)}
          />

          {/* Most Loved Products (single products) */}
          <LeaderboardCard
            title="أكثر المنتجات حبًا"
            icon={<TrendingUp className="h-5 w-5 text-amber-500" />}
            items={data?.most_loved_products}
            renderItem={(product, rank) => (
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                  {rank}
                </div>
                <div className="h-10 w-10 rounded-md overflow-hidden bg-muted shrink-0">
                  <img src={product.first_image} alt={product.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${product.slug}`} className="font-medium hover:underline line-clamp-1">
                    {product.name}
                  </Link>
                  <p className="text-xs text-muted-foreground">{product.loves_count} إعجاب</p>
                  <Link to={`/store/${product.store.slug}`} className="text-xs text-primary hover:underline">
                    {product.store.name}
                  </Link>
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
}

// Reusable leaderboard card component
function LeaderboardCard({ title, icon, items, renderItem }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3 border-b">
        <CardTitle className="flex items-center gap-2 text-xl">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 divide-y">
        {(!items || items.length === 0) ? (
          <div className="p-6 text-center text-muted-foreground">لا توجد بيانات</div>
        ) : (
          items.map((item, idx) => (
            <div key={item.id} className="p-4 hover:bg-muted/30 transition-colors">
              {renderItem(item, idx + 1)}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function HallOfFameSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8 space-y-8" dir="rtl">
      <div className="text-center space-y-3">
        <Skeleton className="h-10 w-64 mx-auto" />
        <Skeleton className="h-5 w-96 mx-auto" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="space-y-4">
            <Skeleton className="h-12 w-48" />
            {[...Array(5)].map((_, j) => (
              <Skeleton key={j} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}